export type VlmModel = {
  id: string
  vlm_profile_id: string
  name: string
  description: string
  focus?: string
  min_required_gpus: number
}

export type PipelineType =
  'directory' | 'http_push' | 'visionplatform' | 'milestone' | 'virtual_patrol'

export type IntegrationType =
  'local_vlm_results' | 'cloud_vlm_results' | 'cloud_event_enrichment'

export type PipelineIntegration = {
  type: IntegrationType
}

export type MilestoneServer = {
  id: string
  name: string
  server_url: string
  username: string
  created_at: string
  updated_at: string
}

export enum PipelineStatus {
  Pending = 1,
  Starting = 2,
  Running = 3,
  Degraded = 4,
  Stopped = 5,
  Failed = 6
}

export type DirectorySource = {
  type: 'directory'
  watch_path: string
  delete_original: boolean
}

export type HttpPushSource = {
  type: 'http_push'
  endpoint_path: string
  port: number
}

export type VpApplicationSourceRead = {
  type: 'visionplatform'
  applications: { id: string; name: string }[]
  upload_descriptions: boolean
}

export type VpApplicationSourceWrite = {
  type: 'visionplatform'
  app_ids: string[]
  upload_descriptions: boolean
}

export type MilestoneSourceRead = {
  type: 'milestone'
  milestone_server: MilestoneServer | null
  preroll_seconds: number
  postroll_seconds: number
  process_when_states: string[]
  cameras: MilestoneCamera[]
}

export type MilestoneSourceWrite = {
  type: 'milestone'
  milestone_server_id: string
  preroll_seconds: number
  postroll_seconds: number
  process_when_states: string[]
  cameras: MilestoneCamera[]
}

// Reserved alert tags of a virtual patrol's round summary. Only email actions of
// virtual patrol pipelines may use them, and never as alert prompts.
export const PATROL_RESERVED_TAGS = [
  'patrol_summary',
  'patrol_incomplete'
] as const

export type PatrolReservedTag = (typeof PATROL_RESERVED_TAGS)[number]

export type MilestoneCamera = {
  camera_id: string
  name: string
  mac_address?: string
}

export type PatrolInterval =
  '15m' | '30m' | '1h' | '2h' | '3h' | '4h' | '6h' | '8h' | '12h' | '24h'

export type PatrolSchedule =
  | {
      mode: 'at'
      at: string[]
      timezone: string
      round_timeout_seconds: number
    }
  | { mode: 'every'; every: PatrolInterval; round_timeout_seconds: number }

export type PatrolClipSeconds = 10 | 20 | 30 | 60

export type PatrolVideoSource = 'milestone' | 'rtsp'

export type VirtualPatrolSourceRead = {
  type: 'virtual_patrol'
  video_source: PatrolVideoSource
  milestone_server: MilestoneServer | null
  cameras: MilestoneCamera[]
  schedule: PatrolSchedule
  clip_seconds: PatrolClipSeconds
}

export type VirtualPatrolSourceWrite = {
  type: 'virtual_patrol'
  video_source: PatrolVideoSource
  milestone_server_id?: string
  cameras: MilestoneCamera[]
  schedule: PatrolSchedule
  clip_seconds: PatrolClipSeconds
}

export type MilestoneServerCamera = {
  camera_id: string
  name: string
  enabled: boolean
  mac_address?: string
}

export type PipelineSource =
  | DirectorySource
  | HttpPushSource
  | VpApplicationSourceRead
  | MilestoneSourceRead
  | VirtualPatrolSourceRead

export type PipelineSourceWrite =
  | DirectorySource
  | HttpPushSource
  | VpApplicationSourceWrite
  | MilestoneSourceWrite
  | VirtualPatrolSourceWrite

export type VlmEventObject = {
  object_id: number
  description: string
  tags: string[]
}

export type VlmResultPayload = {
  type: string
  schema_version: string
  external_event_id: string
  camera_id: string
  source_id: string
  video_id: string
  timestamp: string
  created_at: string
  description: string
  tags: string[]
  alert_tags: string[]
  embedding_model_version: string
  objects: VlmEventObject[]
  metadata: Record<string, unknown> | null
}

export type VlmResult = {
  id: string
  created_at: string
  pipeline_id: string
  // Null when the event outlives its pipeline row.
  pipeline_name: string | null
  pipeline_source: PipelineType | null
  user_prompt?: string | null
  // Signed, ~15 min lifetime. video_url is detail-only.
  video_url?: string | null
  thumbnail_url?: string | null
  result: VlmResultPayload
}

export type AlertSeverity = 'low' | 'medium' | 'high' | 'critical'

export type AlertPrompt = {
  name: string
  description: string
  alert_tag: string
  severity: AlertSeverity
}

export type EmailVlmAction = {
  type: 'email'
  alert_tags: string[]
  recipients: string[]
  cooldown_minutes: number
}

export type MilestoneAlarmState = {
  name: string
  level: number
}

export type MilestoneStateKey = 'pending' | 'verified' | 'closed'

export type MilestoneActionStates = {
  pending: MilestoneAlarmState
  verified: MilestoneAlarmState
  closed: MilestoneAlarmState
}

export type MilestoneVlmAction = {
  type: 'milestone'
  alert_tags: string[]
  milestone_server_id: string
  states: MilestoneActionStates
}

export type VlmAction = EmailVlmAction | MilestoneVlmAction

export type VlmActionType = VlmAction['type']

export type Pipeline = {
  id: string
  name: string
  device_id: string
  device_name: string
  device_active: boolean
  status: PipelineStatus
  status_display: string
  source: PipelineSource
  error_message?: string
  user_prompt?: string
  alert_prompts: AlertPrompt[]
  actions: VlmAction[]
  fps: number
  enable_thinking: boolean
  enable_cloud_results: boolean
  created_at: string
  updated_at: string
}
